import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { Save, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REQUIRED_FORMS = [
  { id: 'enrollment_info', name: 'Enrollment Info', renewalMonths: 12 },
  { id: 'financial_contract', name: 'Financial Contract', renewalMonths: 6 },
  { id: 'about_child', name: 'About Your Child', renewalMonths: 12 },
  { id: 'health_report', name: 'Health Report', renewalMonths: 12 },
  { id: 'shot_records', name: 'Shot Records', renewalMonths: 12 },
  { id: 'emergency_consent', name: 'Emergency Contact/Parental Consent Form', renewalMonths: 6 },
  { id: 'transportation_policy', name: 'Transportation Policy', renewalMonths: 12 }
];

const ON_FILE_FORMS_FOR_SCHOOL_AGE = ['health_report', 'shot_records'];

function getFormsForChild(childCategory) {
  const cat = (childCategory || '').toLowerCase();
  const isSchoolAge = cat.includes('school') || cat === 'after_school';
  if (!isSchoolAge) return REQUIRED_FORMS;
  return REQUIRED_FORMS.map(f =>
    ON_FILE_FORMS_FOR_SCHOOL_AGE.includes(f.id)
      ? { ...f, renewalMonths: 0, renewalLabel: 'On File' }
      : f
  );
}

const calculateExpiration = (dateStr, renewalMonths) => {
  if (!dateStr || dateStr === "N/A" || !renewalMonths) return null;
  try {
    const d = new Date(dateStr + 'T00:00:00');
    if (isNaN(d.getTime())) return null;
    d.setMonth(d.getMonth() + renewalMonths);
    return d.toISOString().split('T')[0];
  } catch {
    return null;
  }
};

const InAppBulkGrid = ({ children: childrenProp, childRecords, onRecordsUpdated, onRefresh, loading }) => {
  const { token } = useAuth();
  const [gridData, setGridData] = useState({});
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const data = {};
    childrenProp.forEach(child => {
      const records = childRecords[child.id] || {};
      data[child.id] = {};
      REQUIRED_FORMS.forEach(form => {
        const rec = records[form.id] || {};
        const lastSubmitted = rec.lastSubmitted || "";
        const isNA = rec.notApplicable || lastSubmitted === "N/A";
        data[child.id][form.id] = { value: isNA ? "N/A" : lastSubmitted, isNA };
      });
    });
    setGridData(data);
    setPendingChanges({});
  }, [childrenProp, childRecords]);

  const handleCellChange = (childId, formId, newValue) => {
    setGridData(prev => ({
      ...prev,
      [childId]: { ...prev[childId], [formId]: { value: newValue, isNA: false } }
    }));
    setPendingChanges(prev => ({
      ...prev,
      [`${childId}:${formId}`]: { childId, formId, value: newValue, isNA: false }
    }));
  };

  const handleSaveAll = async () => {
    const changes = Object.values(pendingChanges);
    if (changes.length === 0) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      const byChild = {};
      changes.forEach(c => {
        if (!byChild[c.childId]) byChild[c.childId] = {};
        const child = childrenProp.find(ch => ch.id === c.childId);
        const forms = getFormsForChild(child?.category);
        const form = forms.find(f => f.id === c.formId);
        if (c.value) {
          byChild[c.childId][c.formId] = { lastSubmitted: c.value, expirationDate: calculateExpiration(c.value, form?.renewalMonths || 12), outstanding: false, notApplicable: false, updatedAt: new Date().toISOString() };
        }
      });
      let saved = 0;
      for (const [childId, forms] of Object.entries(byChild)) {
        const existing = childRecords[childId] || {};
        const merged = { ...existing };
        for (const [formId, formData] of Object.entries(forms)) {
          merged[formId] = { ...(merged[formId] || {}), ...formData };
        }
        await axios.put(`${API}/children-records/${childId}`, { forms: merged });
        saved++;
      }
      setPendingChanges({});
      toast.success(`Saved changes for ${saved} children`);
      if (onRecordsUpdated) onRecordsUpdated();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    if (hasPending) {
      if (!window.confirm("You have unsaved changes. Refresh will discard them. Continue?")) return;
    }
    if (onRefresh) onRefresh();
  };

  const hasPending = Object.keys(pendingChanges).length > 0;
  const childName = (child) => child.name || `${child.firstName || ''} ${child.lastName || ''}`.trim();

  return (
    <div className="space-y-3" data-testid="in-app-bulk-grid">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            Edit dates directly. Enter the date a form was received — leave blank for forms not yet received.
            {hasPending && <span className="ml-2 text-orange-600 font-medium">({Object.keys(pendingChanges).length} unsaved changes)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleRefresh} disabled={loading} variant="outline" size="sm" className="border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="refresh-grid-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
          <Button onClick={handleSaveAll} disabled={!hasPending || saving} className={hasPending ? "bg-green-600 hover:bg-green-700" : "bg-gray-400"} data-testid="save-grid-btn">
            <Save className="w-4 h-4 mr-1" />
            {saving ? "Saving..." : "Save All Changes"}
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8 text-teal-600">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          <span className="text-sm">Loading children and dates...</span>
        </div>
      )}

      {!loading && (
        <div className="overflow-auto border rounded-lg max-h-[65vh]">
          <table className="text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-teal-700 text-white">
                <th className="sticky left-0 z-20 bg-teal-700 px-3 py-2 text-left border-r border-teal-600 whitespace-nowrap" style={{ minWidth: 180 }}>
                  Child's Name
                </th>
                {REQUIRED_FORMS.map(form => (
                  <th key={form.id} className="px-2 py-2 text-center border-r border-teal-600 font-medium whitespace-nowrap" style={{ minWidth: 140 }}>
                    {form.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {childrenProp.map((child, rowIdx) => (
                <tr key={child.id} className={rowIdx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className={`sticky left-0 z-[5] px-3 py-1.5 font-semibold text-teal-800 border-r border-gray-200 whitespace-nowrap ${rowIdx % 2 === 0 ? 'bg-teal-50' : 'bg-teal-50'}`} style={{ minWidth: 180 }}>
                    {childName(child)}
                  </td>
                  {REQUIRED_FORMS.map(form => {
                    const cell = gridData[child.id]?.[form.id] || { value: "", isNA: false };
                    const isPending = pendingChanges[`${child.id}:${form.id}`];
                    return (
                      <td key={form.id} className={`px-1 py-1 border-r border-gray-200 text-center ${isPending ? 'bg-yellow-50' : ''}`}>
                        <div className="flex items-center justify-center">
                          <input
                            type="date"
                            value={(cell.isNA ? "" : cell.value) || ""}
                            onChange={(e) => handleCellChange(child.id, form.id, e.target.value)}
                            className="border rounded px-1 py-0.5 text-xs w-[105px] focus:ring-1 focus:ring-teal-400 focus:border-teal-400"
                            data-testid={`grid-${child.id}-${form.id}`}
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0 z-10">
              <tr className="bg-gray-100 border-t-2 border-teal-700 font-semibold text-xs">
                <td className="sticky left-0 z-[5] bg-gray-100 px-3 py-2 text-teal-800 border-r border-gray-300 whitespace-nowrap" style={{ minWidth: 180 }}>
                  Missing Count
                </td>
                {REQUIRED_FORMS.map(form => {
                  const missingCount = childrenProp.filter(child => {
                    const cell = gridData[child.id]?.[form.id];
                    return !cell?.isNA && !cell?.value;
                  }).length;
                  return (
                    <td key={form.id} className="px-2 py-2 text-center border-r border-gray-300" style={{ minWidth: 140 }}>
                      {missingCount > 0
                        ? <span className="inline-block bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded">{missingCount} missing</span>
                        : <span className="inline-block bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded">All on file</span>
                      }
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default InAppBulkGrid;
